import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Panel } from '@usacow/ds';
import { api, type Profile } from '../lib/api';
import { useAuth } from '../lib/auth';

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="set-toggle">
      <span>
        <b>{label}</b>
        {hint && <small>{hint}</small>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export function Settings() {
  const { user, profile, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function patch(p: Partial<Profile>) {
    setBusy(true);
    await api.patchProfile(p).catch(() => {});
    await refresh();
    setBusy(false);
  }

  function applyTheme(theme: string) {
    document.documentElement.setAttribute('data-theme', theme);
    void patch({ theme });
  }

  async function onDelete() {
    if (!window.confirm('Delete your account and all data? This cannot be undone.')) return;
    await api.deleteAccount().catch(() => {});
    await signOut();
    navigate('/');
  }

  const p = profile;

  return (
    <div className="scr scr--narrow">
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">account &amp; settings</div>
          <h1 className="scr-h">{user?.display_name ?? user?.email ?? 'Your account'}</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void signOut().then(() => navigate('/'))}>
          Sign out
        </Button>
      </div>

      <Panel title="Accessibility" style={{ marginBottom: 14 }}>
        <Toggle
          label="Reduce motion"
          hint="Disable non-essential animation"
          checked={Boolean(p?.reduce_motion)}
          onChange={(v) => patch({ reduce_motion: v ? 1 : 0 })}
        />
        <Toggle
          label="Dyslexia-friendly spacing"
          hint="Wider statement text and letter spacing"
          checked={Boolean(p?.dyslexia_spacing)}
          onChange={(v) => patch({ dyslexia_spacing: v ? 1 : 0 })}
        />
        <Toggle
          label="High contrast"
          hint="Stronger borders and text contrast"
          checked={Boolean(p?.high_contrast)}
          onChange={(v) => patch({ high_contrast: v ? 1 : 0 })}
        />
        <div className="set-row">
          <span>
            <b>Theme</b>
            <small>Dark or light</small>
          </span>
          <div className="set-theme">
            <Button
              variant={p?.theme === 'dark' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => applyTheme('dark')}
            >
              Dark
            </Button>
            <Button
              variant={p?.theme === 'light' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => applyTheme('light')}
            >
              Light
            </Button>
          </div>
        </div>
      </Panel>

      <Panel title="Notifications" style={{ marginBottom: 14 }}>
        <Toggle
          label="Review reminders"
          hint="When spaced-review cards are due"
          checked={Boolean(p?.notify_review_due)}
          onChange={(v) => patch({ notify_review_due: v ? 1 : 0 })}
        />
        <Toggle
          label="Assignments"
          hint="When a coach assigns work"
          checked={Boolean(p?.notify_assignments)}
          onChange={(v) => patch({ notify_assignments: v ? 1 : 0 })}
        />
        <p className="dim set-note">usaCow never sells data or markets using student work.</p>
      </Panel>

      {import.meta.env.DEV && (
        <Panel title="Dev tools" style={{ marginBottom: 14 }}>
          <div className="set-row">
            <span>
              <b>Role</b>
              <small>Switch role to exercise coach/admin surfaces (local only)</small>
            </span>
            <div className="set-theme">
              {['student', 'coach', 'admin'].map((r) => (
                <Button
                  key={r}
                  variant="ghost"
                  size="sm"
                  onClick={() => api.devRole(r).then(() => refresh())}
                >
                  {r}
                </Button>
              ))}
            </div>
          </div>
        </Panel>
      )}

      <Panel title="Your data" actions={busy ? <span className="dim">saving…</span> : undefined}>
        <div className="set-data">
          <div>
            <b>Export</b>
            <small className="dim">Download everything we hold about you as JSON.</small>
          </div>
          <Button variant="secondary" size="sm" onClick={() => api.exportData()}>
            Export data
          </Button>
        </div>
        <div className="set-data set-data--danger">
          <div>
            <b>Delete account</b>
            <small className="dim">Permanently remove your account and all data.</small>
          </div>
          <Button variant="danger" size="sm" onClick={onDelete}>
            Delete account
          </Button>
        </div>
      </Panel>
    </div>
  );
}
